import { updatePaymentMethod } from "@/api/rest/checkoutApi";
import {
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import { TABLET_SCREEN_SIZE } from "@brandclub/common-ui";

import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  LineItem,
  PaymentMethod,
  ShippingRate,
  StripeError,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
} from "@stripe/stripe-js";
import { useState } from "react";
import { useCheckoutUI } from "../Checkout/components/CheckoutV2/CheckoutUIProvider";
import { CheckoutIntent } from "../Checkout/components/CheckoutV2/types/CheckoutIntent";
import { StoreError } from "../StoreComponents/StoreError";
import { styled } from "@mui/material";

// Add global declaration for the Google Pay sheet on window
declare global {
  interface Window {
    gpay?: {
      close: () => void;
    };
  }
}
// // Helper to detect Safari browser
// const isSafari = () => {
//   const ua = navigator.userAgent.toLowerCase();
//   return ua.indexOf("safari") !== -1 && ua.indexOf("chrome") === -1;
// };

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

export const enum ElementType {
  expressCheckout = "expressCheckout",
  card = "card",
}

const useStripePaymentProcess = (elementType: ElementType) => {
  const stripe = useStripe();
  const elements = useElements();
  const [state, send] = useCheckoutUI();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethodError, setPaymentMethodError] =
    useState<string | null>(null);

  const createPaymentMethod = async () => {
    if (!stripe || !elements) {
      return {
        error: new Error("Stripe or elements not initialized"),
        paymentMethod: null,
        isProcessing: false,
      };
    }

    setIsProcessing(true);
    setPaymentMethodError(null);

    try {
      // Submit the form first
      const submitResult = await elements.submit();
      if (submitResult.error) {
        throw new Error(
          submitResult.error.message || "Failed to submit payment details"
        );
      }

      let error: StripeError | undefined = undefined;
      let paymentMethod: PaymentMethod | undefined = undefined;
      if (elementType === ElementType.expressCheckout) {
        // Create payment method
        ({ error, paymentMethod } = await stripe.createPaymentMethod({
          elements,
        }));
      } else {
        // We have to do it this way because there is a bug in Stripe
        // that will not let a user add a card after using the express checkout in the same session
        ({ error, paymentMethod } = await stripe.createPaymentMethod({
          element: elements.getElement("payment")!,
        }));
      }

      if (error) {
        throw new Error(error.message || "Failed to create payment method");
      }

      if (!paymentMethod) {
        throw new Error("No payment method created");
      }

      return {
        paymentMethod,
        error: null,
        isProcessing: false,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      setPaymentMethodError(errorMessage);
      return {
        error: error instanceof Error ? error : new Error(errorMessage),
        paymentMethod: null,
        isProcessing: false,
      };
    } finally {
      setIsProcessing(false);
    }
  };

  const processPayment = async ({
    paymentMethod,
    shippingAddress,
    billingDetails,
  }: {
    paymentMethod: PaymentMethod;
    shippingAddress?: CheckoutIntent["shippingAddress"];
    billingDetails?: CheckoutIntent["billingAddress"];
  }) => {
    try {
      setIsProcessing(true);
      const context = state?.context || {};
      // @ts-ignore - bypass type checking for context access
      const checkoutIntentId = context.checkoutIntent?.checkoutIntentId;

      if (checkoutIntentId) {
        const {
          checkoutIntent: updatedCheckoutIntent,
          status,
          errorMessage,
        } = await updatePaymentMethod({
          checkoutIntentId,
          paymentMethodId: paymentMethod.id,
          shippingAddress: shippingAddress as any,
          billingAddress: billingDetails as any,
        });

        if (status === "success") {
          send({
            type: "SET_CHECKOUT_INTENT",
            data: { checkoutIntent: updatedCheckoutIntent },
          });
          return true;
        } else {
          setPaymentMethodError(
            errorMessage || "Failed to update payment method"
          );
          return false;
        }
      } else {
        return true;
      }
    } catch (error) {
      setPaymentMethodError(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    createPaymentMethod,
    processPayment,
    isProcessing,
    paymentMethodError,
    clearPaymentMethodError: () => setPaymentMethodError(null),
  };
};

export const StripeExpressCheckout = ({
  onSuccess,
  onError,
  onShippingAddressChange,
  onShippingRateChange,
  shippingRates,
  lineItems,
}: {
  onSuccess: (paymentMethod: PaymentMethod) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
  lineItems: LineItem[];
}) => {
  const {
    createPaymentMethod,
    processPayment,
    paymentMethodError,
    clearPaymentMethodError,
  } = useStripePaymentProcess(ElementType.expressCheckout);

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      clearPaymentMethodError();
      // console.log("stripe express checkout onConfirm event", event);
      const result = await createPaymentMethod();
      if (result.error) {
        onError(result.error);
        return;
      }
      if (result.paymentMethod) {
        const shippingAddress = event.shippingAddress;
        const billingDetails = event.billingDetails;
        let shippingAddressToSubmit: CheckoutIntent["shippingAddress"] =
          undefined;
        if (shippingAddress && shippingAddress.address.line1) {
          shippingAddressToSubmit = {
            address1: shippingAddress.address.line1,
            address2: shippingAddress.address.line2 ?? "",
            city: shippingAddress.address.city,
            state: shippingAddress.address.state,
            zipCode: shippingAddress.address.postal_code,
            country: shippingAddress.address.country,
            firstName: shippingAddress.name.split(" ")[0],
            lastName: shippingAddress.name.split(" ")[1],
            phoneNumber: billingDetails?.phone ?? "",
            email: billingDetails?.email ?? "",
          };
        }
        let billingAddressToSubmit: CheckoutIntent["billingAddress"] =
          undefined;
        if (billingDetails && billingDetails.address.line1) {
          billingAddressToSubmit = {
            address1: billingDetails.address.line1,
            address2: billingDetails.address.line2 ?? "",
            city: billingDetails.address.city,
            state: billingDetails.address.state,
            zipCode: billingDetails.address.postal_code,
            country: billingDetails.address.country,
            firstName: billingDetails.name.split(" ")[0],
            lastName: billingDetails.name.split(" ")[1],
            email: billingDetails.email,
            phoneNumber: billingDetails.phone,
          };
        }

        if (
          await processPayment({
            paymentMethod: result.paymentMethod,
            shippingAddress: shippingAddressToSubmit,
            billingDetails: billingAddressToSubmit,
          })
        ) {
          onSuccess(result.paymentMethod);
        }
      }
    } catch (error) {
      onError(error as Error);
    }
  };

  return (
    <Container className="checkout-express-payment-container">
      <ExpressCheckoutElement
        className="express-checkout-element"
        options={{
          business: {
            name: "Brandclub",
          },
          buttonType: {
            applePay: "buy",
            googlePay: "buy",
          },
          buttonTheme: {
            applePay: "white-outline", // TODO: need to fix the border cut off issue then use white-outline
            googlePay: "white",
          },
          emailRequired: true,
          phoneNumberRequired: true,
          shippingAddressRequired: true,
          billingAddressRequired: true,
          paymentMethods: {
            applePay: "always",
            googlePay: "always",
          },
          shippingRates,
          lineItems,
        }}
        onShippingAddressChange={onShippingAddressChange}
        onShippingRateChange={onShippingRateChange}
        onConfirm={onConfirm}
      />
      {paymentMethodError && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <StoreError
            errorMessage={paymentMethodError}
            sx={{ textAlign: "center", width: "fit-content" }}
          />
        </div>
      )}
    </Container>
  );
};

const CardCheckoutContainer = styled("div")(({ theme }) => ({
  padding: "0 20px 20px 20px",
  display: "flex",
  flexDirection: "column",
  width: 550,
  boxSizing: "border-box",
  maxWidth: "100%",

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: "20px 20px 20px 20px",
  },

  ".payment_form_title": {
    fontSize: "16px",
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    marginBottom: "16px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      textAlign: "center",
    },
  },

  ".buttonGroup": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "22px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      marginTop: "12px",
      gap: "12px",
      flexDirection: "column-reverse",
    },
  },
}));

export const StripeCardCheckout = ({
  onSuccess,
  onError,
  onCancel,
}: {
  onCancel: () => void;
  onSuccess: (paymentMethod: PaymentMethod) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
}) => {
  const user = useAppSelector((state) => state.userProfile);
  const {
    createPaymentMethod,
    processPayment,
    isProcessing,
    paymentMethodError,
    clearPaymentMethodError,
  } = useStripePaymentProcess(ElementType.card);
  const [isPaymentMethodLoaded, setIsPaymentMethodLoaded] = useState(false);
  const [isPaymentMethodCompleted, setPaymentMethodCompleted] = useState(false);

  const handlePaymentSubmit = async () => {
    try {
      const result = await createPaymentMethod();
      clearPaymentMethodError();
      if (result.error) {
        onError(result.error);
        return;
      }
      if (result.paymentMethod) {
        if (
          await processPayment({
            paymentMethod: result.paymentMethod,
          })
        ) {
          onSuccess(result.paymentMethod);
        }
      }
    } catch (error) {
      onError(error as Error);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <CardCheckoutContainer className="stripe-card-checkout-container">
      <div className="payment_form_title">Add new credit card</div>
      <PaymentElement
        onReady={() => {
          setIsPaymentMethodLoaded(true);
        }}
        onChange={(event) => {
          setPaymentMethodCompleted(event.complete);
        }}
        options={{
          fields: {
            billingDetails: {
              name: "auto",
              email: "auto",
              phone: "auto",
              address: {
                country: "auto",
                postalCode: "auto",
                state: "auto",
                city: "auto",
                line1: "auto",
                line2: "auto",
              },
            },
          },
          defaultValues: {
            billingDetails: {
              name: `${user.given_name} ${user.family_name}`,
              email: user.email,
              phone: user.phoneNumber,
            },
          },
        }}
      />
      {paymentMethodError && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <StoreError
            errorMessage={paymentMethodError}
            sx={{ textAlign: "center", width: "fit-content" }}
          />
        </div>
      )}
      <div className="buttonGroup">
        <OutlineButton
          height={{ xs: 37 }}
          sx={{
            width: "unset",
            minWidth: "83px",
            [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
              width: "100%",
              maxWidth: "100%",
            },
          }}
          onClick={onCancel}
          disabled={isProcessing || !isPaymentMethodLoaded}
        >
          Cancel
        </OutlineButton>
        <SolidButton
          height={{ xs: 37 }}
          sx={{
            width: "unset",
            minWidth: "83px",
            [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
              width: "100%",
              maxWidth: "100%",
            },
          }}
          onClick={handlePaymentSubmit}
          disabled={
            isProcessing || !isPaymentMethodLoaded || !isPaymentMethodCompleted
          }
        >
          {isProcessing ? "Saving" : "Save"}
        </SolidButton>
      </div>
    </CardCheckoutContainer>
  );
};

export const StripeCheckout = ({
  onSuccess,
  onError,
  onShippingAddressChange,
  onShippingRateChange,
  shippingRates,
  lineItems,
  onCancel,
}: {
  onCancel: () => void;
  onSuccess: (paymentMethodType: string, paymentMethod: PaymentMethod) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
  lineItems: LineItem[];
}) => {
  const user = useAppSelector((state) => state.userProfile);

  if (!user) {
    return null;
  }

  return (
    <>
      <div style={{ marginBottom: "20px" }}>
        <StripeExpressCheckout
          onSuccess={(paymentMethod) => onSuccess("express", paymentMethod)}
          onError={onError}
          onShippingAddressChange={onShippingAddressChange}
          onShippingRateChange={onShippingRateChange}
          shippingRates={shippingRates}
          lineItems={lineItems}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          margin: "20px 0",
          color: "#666",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: "#dcdddc",
          }}
        />
        <span
          style={{
            margin: "0 16px",
            fontSize: "14px",
          }}
        >
          Add new credit card
        </span>
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: "#dcdddc",
          }}
        />
      </div>
      <div>
        <StripeCardCheckout
          onSuccess={(paymentMethod) => onSuccess("card", paymentMethod)}
          onError={onError}
          onShippingAddressChange={onShippingAddressChange}
          onShippingRateChange={onShippingRateChange}
          shippingRates={shippingRates}
          onCancel={onCancel}
        />
      </div>
    </>
  );
};
